// @ts-nocheck
/* eslint-disable react/prop-types */
/**
 * OffsetEdge — Offset path edge for bidirectional connections.
 *
 * Uses an offset on the bezier path to avoid overlap
 * with a reverse edge going the opposite direction.
 *
 * Supports:
 *   data.color       — stroke colour override (theme tokens supported)
 *   data.offset      — custom bezier curvature/offset
 *   data.text / label — optional label text
 */
import React, { memo } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  useStore,
} from 'reactflow';
import { useTheme } from '@mui/material/styles';
import SolyticsBox from '../../../SolyticsBox';
import SolyticsTypography from '../../../SolyticsTypography';
import NodeTooltip from '../shared/NodeTooltip';
import { resolveThemeColor } from '../shared/themeColorUtils';

const OffsetEdge = memo(
  ({
    id,
    source,
    target,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    markerEnd,
    markerStart,
    label,
    data,
  }) => {
    const theme = useTheme();

    const isBiDirectionEdge = useStore((s) => {
      return s.edges.some(
        (e) =>
          (e.source === target && e.target === source) ||
          (e.target === source && e.source === target && e.id !== id)
      );
    });

    let edgePath, labelX, labelY;

    if (isBiDirectionEdge) {
      // Create a pristine symmetrical "eye" arc for bidirectional edges
      // Fallback explicitly to the React Flow docs Quadratic Bezier implementation
      const centerX = (sourceX + targetX) / 2;
      const centerY = (sourceY + targetY) / 2;
      const arcOffset = data?.offset ?? (sourceX < targetX ? 25 : -25);

      edgePath = `M ${sourceX} ${sourceY} Q ${centerX} ${
        centerY + arcOffset
      } ${targetX} ${targetY}`;

      labelX = centerX;
      labelY = centerY + arcOffset / 2;
    } else {
      [edgePath, labelX, labelY] = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
      });
    }

    const strokeColor = resolveThemeColor(
      theme,
      data?.color,
      'solyticsFlow.edge.defaultStroke'
    );
    const displayLabel = label || data?.text;
    const labelColor = resolveThemeColor(
      theme,
      data?.labelColor,
      'solyticsFlow.edge.labelColor'
    );

    return (
      <>
        <BaseEdge
          id={id}
          path={edgePath}
          markerEnd={markerEnd}
          markerStart={markerStart}
          style={{
            stroke: strokeColor || theme.palette.text.primary,
            strokeWidth: 1.5,
          }}
        />
        {displayLabel && (
          <EdgeLabelRenderer>
            <NodeTooltip tooltip={data?.labelTooltip}>
              <SolyticsBox
                className="nodrag nopan"
                position="absolute"
                bgcolor={theme.palette.background.paper}
                px="8px"
                py="2px"
                borderRadius="4px"
                maxWidth="120px"
                overflow="hidden"
                sx={{
                  transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                  pointerEvents: 'all',
                }}
              >
                <SolyticsTypography
                  variant="caption"
                  color={labelColor || theme.palette.text.secondary}
                  noWrap
                >
                  {displayLabel}
                </SolyticsTypography>
              </SolyticsBox>
            </NodeTooltip>
          </EdgeLabelRenderer>
        )}
      </>
    );
  }
);
OffsetEdge.displayName = 'OffsetEdge';

export default OffsetEdge;
